import { HttpService } from "../http"
import { ApiRoutes } from "../routes"
import { SessionService } from "../session"
import { Logger } from "../logger"
import { LoginResponse, LoginRequest, LoginError, ChangePasswordResponse, ChangePasswordRequest } from "../../models/login"
import { Terminal } from "../../models/terminal"
import { Franchise } from "../../models/common/franchise"
import { CreateFranchise } from "../../models/transport/request/createfranchise"
import { UpdateFranchise } from "../../models/transport/request/UpdateFranchise"
import { container } from "../../../infrastructure/container"

/**
 * Interface defining login, terminal, and franchise functionality.
 */
export interface FranchiseService {
  /**
   * Logs in a user with the provided credentials.
   * @param email - User's email.
   * @param password - User's password.
   * @returns A promise resolving to the login response.
   */
  login(email: string, password: string): Promise<LoginResponse>

  /**
 * Sends a password reset email.
 * @param {string} email - The user's email.
 * @returns {Promise<any>} API response for password reset request.
 */
  forgotPassword(email: string): Promise<{ message: string }>

  /**
   * Creates a new terminal.
   * @param terminal - Terminal details to be created.
   * @returns A promise resolving to the created terminal.
   */
  createTerminal(terminal: Terminal): Promise<Terminal>

  /**
  *  Updates a TERMINAL Details.
  * @param id - The unique identifier of the TERMINAL to delete.
  * @returns A promise resolving to a success message.
  */
  updateTerminal(payload: Terminal): Promise<Terminal>

  /**
 * Deletes a TERMINAL by id.
 * @param id - The unique identifier of the TERMINAL to delete.
 * @returns A promise resolving to a success message.
 */
  deleteTerminal(id: string): Promise<{ message: string }>

  /**
   * Retrieves a terminals data.
   * @param terminal - Terminal details to be fetched.
   * @returns A promise resolving to an array of terminal data.
   */
  getTerminalDetails(terminalId: string): Promise<Terminal>

  /**
   * Creates a new franchise.
   * @param franchise - Franchise details to be created.
   * @returns A promise resolving to the created franchise.
   */
  createFranchise(franchise: CreateFranchise): Promise<Franchise>

  /**
   * Retrieves a list of franchises.
   * @returns A promise resolving to an array of franchises.
   */
  getFranchises(): Promise<Franchise[]>

  /**
 * Retrieves a franchises by id.
 * @returns A promise resolving to an array of franchises.
 */
  getFranchise(id: string): Promise<Franchise>

  /**
   * Deletes a franchises by id.
   * @param id - The unique identifier of the franchise to delete.
   * @returns A promise resolving to a success message.
   */
  deleteFranchise(id: string): Promise<{ message: string }>

  /**
   * Updates a franchise with the provided payload.
   *
   * @param payload - The data used to update the franchise. Must include `franchiseId` and any fields to be updated.
   * @returns A promise that resolves to the updated `Franchise` object.
   */
  updateFranchise(payload: UpdateFranchise): Promise<Franchise>

  /**
  * Changes the user's password.
  * @param currentPassword - The user's current password.
  * @param newPassword - The new password to set.
  * @returns A promise resolving to a success message.
  *
  *
  */
  changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }>
  /**
   * Logs out the current user and clears session data.
   * @returns A promise that resolves when logout is complete.
   */
  logout(): Promise<void>
}

/**
 * Implementation of `AppFranchiseService` that interacts with the backend API.
 */
export class AppFranchiseService implements FranchiseService {
  /**
   * Initializes the login service with the HTTP service instance.
   * @param httpService - The HTTP service to send API requests.
   */
  private httpService = container.resolve<HttpService>("httpService")
  private sessionService = container.resolve<SessionService>("sessionService")
  private logger = container.resolve<Logger>("logger")

  /**
   * Logs in a user and stores session data.
   * @param email - User's email.
   * @param password - User's password.
   * @returns A promise resolving to the login response.
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const body: LoginRequest = { email, password }
    const response = await this.httpService.post<LoginRequest, LoginResponse, LoginError>
      (
        ApiRoutes.LOGIN,
        body
      )

    // Store user data in session
    this.sessionService.user = response.user
    this.sessionService.setFranchisesBrief(response.franchises)

    return response
  }

  async forgotPassword(email: string): Promise<ChangePasswordResponse> {
    const body: LoginRequest = { email }
    return await this.httpService.post<LoginRequest, ChangePasswordResponse, LoginError>
      (
        ApiRoutes.PASSWORD_RESET,
        body
      )
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
    try {
      const body: ChangePasswordRequest = { currentPassword, newPassword }

      return await this.httpService.post<ChangePasswordRequest, ChangePasswordResponse, LoginError>
        (
          ApiRoutes.CHANGE_PASSWORD,
          body
        )
    } catch (error) {
      this.logger.error("Error changing password:", error)
      throw error
    }
  }

  /**
   * Creates a new terminal and updates session data.
   * @param terminal - Terminal details to be created.
   * @returns A promise resolving to the created terminal.
   */
  async createTerminal(terminal: Terminal): Promise<Terminal> {
    try {
      const response = await this.httpService.post<Terminal, Terminal, LoginError>(
        ApiRoutes.CREATE_TERMINAL,
        terminal
      )

      if (response) {
        this.sessionService.addTerminal(response)

        // Update the franchise's terminals in the session
        const franchises = this.sessionService.franchises
        const franchise = franchises.find(
          (f) => f.franchiseId === terminal.franchiseId
        )
        if (franchise) {
          if (!franchise.terminals) {
            franchise.terminals = []
          }
          franchise.terminals.push(response) // push full Terminal object
          this.sessionService.setFranchises(franchises)
        }
      }

      return response
    } catch (error) {
      this.logger.error("Error creating terminal:", error)
      throw error
    }
  }

  /**
   * Get terminal Details and updates session data.
   * @param terminal - Terminal details Displayed.
   * @returns A promise resolving to the get terminal.
   */
  async getTerminalDetails(terminalId: string): Promise<Terminal> {
    if (!terminalId) {
      throw new Error("Terminal ID is required")
    }

    const response = await this.httpService.get<Terminal, LoginError>(ApiRoutes.TERMINAL_DETAILS, { terminalId })

    return {
      ...response,
      franchiseId: response.franchiseId,
    }
  }

  /**
   * Delete terminals.
   * @param terminal - Delete Terminals.
   * @returns A promise resolving success message terminal deleted.
   */
  async deleteTerminal(terminalId: string): Promise<{ message: string }> {
    try {
      const route = `${ApiRoutes.DELETE_TERMINAL}/${terminalId}`
      return await this.httpService.delete<{ message: string }, LoginError>(route)
    } catch (error) {
      this.logger.error("Error deleting terminal:", error)
      throw error
    }
  }

  /**
   * Creates a new franchise and updates session data.
   * @param franchise - Franchise details to be created.
   * @returns A promise resolving to the created franchise.
   */
  async createFranchise(franchise: CreateFranchise): Promise<Franchise> {
    try {
      const response = await this.httpService.post<CreateFranchise, Franchise, LoginError>
        (
          ApiRoutes.CREATE_FRANCHISE,
          franchise
        )

      if (response) {
        this.sessionService.addFranchise(response) // Store in session
      }

      return response
    } catch (error) {
      this.logger.error("Error creating franchise:", error)
      throw error
    }
  }

  /**
   * Fetches all franchises from the API.
   * @returns A promise resolving to an array of franchises.
   */
  async getFranchises(): Promise<Franchise[]> {
    console.log("Making API call to fetch franchises...")
    try {
      const response = await this.httpService.get<Franchise[], LoginError>(
        ApiRoutes.GET_FRANCHISES
      )
      console.log("API response received:", response)
      return response
    } catch (error) {
      console.error("Error in getFranchises API call:", error)
      this.logger.error("Error fetching franchises:", error)
      throw error
    }
  }

  async getFranchise(id: string): Promise<Franchise> {
    console.log("Making API call to fetch franchises...")
    try {
      const response = await this.httpService.get<Franchise, LoginError>(
        ApiRoutes.GET_FRANCHISES,
        { id }
      )
      console.log("API response received:", response)
      return response
    } catch (error) {
      console.error("Error in getFranchises API call:", error)
      this.logger.error("Error fetching franchises:", error)
      throw error
    }
  }

  /**
  * Updates a franchise and syncs session data.
  * @param franchise - Franchise update payload.
  * @returns A promise resolving to the updated franchise.
  */
  async updateFranchise(payload: UpdateFranchise): Promise<Franchise> {
    try {
      const response = await this.httpService.post<UpdateFranchise, Franchise, LoginError>(
        ApiRoutes.UPDATE_FRANCHISE,
        payload
      )
      console.log("payload info", payload)
      return response
    } catch (error) {
      this.logger.error("Error updating franchise:", error)
      throw error
    }
  }

  async updateTerminal(payload: Terminal): Promise<Terminal> {
    try {
      const response = await this.httpService.post<Terminal, Terminal, LoginError>(
        ApiRoutes.TERMINAL_UPDATE,
        payload
      )
      console.log("payload info", payload)
      return response
    } catch (error) {
      this.logger.error("Error updating franchise:", error)
      throw error
    }
  }

  async deleteFranchise(franchiseId: string): Promise<{ message: string }> {
    try {
      const route = `${ApiRoutes.DELETE_FRANCHISE}/${franchiseId}`
      const response = await this.httpService.delete<{ message: string }, LoginError>(route)
      this.logger.info("Franchise deleted:", response.message)
      return response
    } catch (error) {
      this.logger.error("Error deleting franchise:", error)
      throw error
    }
  }

  /**
   * Logs out the user and clears session data.
   * @returns A promise that resolves when the logout process is complete.
   */
  async logout(): Promise<void> {
    try {
      // You can call the backend logout endpoint if needed
      await this.httpService.post(ApiRoutes.LOGOUT, {});

      this.sessionService.resetStorage() // Clear session data
      console.log("User logged out")
    } catch (error) {
      this.logger.error("Logout failed", error)
    }
  }
}